<script setup lang="ts">
import { computed, useTemplateRef, type InputHTMLAttributes } from 'vue';
import type { AlignedTextInputProps } from './internal';
import NxInput from './NxInput.vue';

withDefaults(
  defineProps<
    AlignedTextInputProps & {
      /** Keep long plain-cell text clear of its measured trailing action rail. */
      reservePlainCellActionSpace?: boolean;
      /**
       * Shows the value in capitals while it is typed (a code such as a
       * country or currency key). Only the look: the model keeps what was
       * typed, so a consumer that stores capitals converts the value itself.
       */
      uppercase?: boolean;
      /**
       * @internal Kit composites that handle Escape themselves set `false`
       * (see NxInput). Not for apps.
       */
      escapeRestores?: boolean;
      /**
       * The value is not valid (a validation finding, e.g. a range that does
       * not parse): the field takes the kit's error border and the input
       * `aria-invalid="true"`. Not a save status — that is NxLoadingWrapper's.
       * Defaults to `false`.
       */
      invalid?: boolean;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>
  >(),
  { align: 'start', escapeRestores: true }
);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

const inputRef = useTemplateRef('input-element');
const exposeRef = computed(() => {
  return inputRef.value?.inputElement;
});
defineExpose({
  inputElement: exposeRef,
  wrapperElement: computed(() => inputRef.value?.wrapperElement ?? null),
});
</script>
<template>
  <NxInput
    v-if="valueModelModifiers.lazy"
    :id="$props.id"
    ref="input-element"
    v-model.lazy="valueModel"
    type="text"
    :icons="$props.icons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
    :align="$props.align"
    :uppercase="$props.uppercase"
    :reserve-plain-cell-action-space="$props.reservePlainCellActionSpace"
    :escape-restores="$props.escapeRestores"
    :invalid="$props.invalid"
  />
  <NxInput
    v-else
    :id="$props.id"
    ref="input-element"
    v-model="valueModel"
    type="text"
    :icons="$props.icons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
    :align="$props.align"
    :uppercase="$props.uppercase"
    :reserve-plain-cell-action-space="$props.reservePlainCellActionSpace"
    :escape-restores="$props.escapeRestores"
    :invalid="$props.invalid"
  />
</template>
